import React, { useMemo } from "react";
import { createPortal } from "react-dom";
import { MdTune, MdCheck } from "react-icons/md";
import useFilterOptions from "../../customHook/FilterOptions/useFilterOptions";

/**
 * FilterOptions - Purely Dynamic & Reusable Filter Component (UI Layer)
 *
 * @param {Array} filters - Array of filter definitions from API
 * @param {Function} onFilterChange - Callback returning selected filters object
 * @param {string} gender - (Optional) Gender for fallback API fetch if `filters` is not provided
 * @param {string} modelType - (Optional) Model type for fallback API fetch
 * @param {string} className - Optional container styling
 */
const FilterOptions = ({
  filters = null,
  gender,
  modelType,
  onFilterChange,
  className = "",
}) => {
  const {
    buttonRef,
    popoverRef,
    isOpen,
    toggleOpen,
    popoverStyle,
    filterGroups,
    activeTabKey,
    setActiveTabKey,
    activeGroup,
    tempFilters,
    loading,
    error,
    totalAppliedCount,
    getTabSelectedCount,
    normalizeOption,
    handleOptionToggle,
    handleClearActiveTab,
    handleClearFilters,
    handleApplyFilters,
    handleResetAll,
    isGroupMultiple,
  } = useFilterOptions({
    filters,
    gender,
    modelType,
    onFilterChange,
  });

  // Sort right-panel filter options alphabetically (A-Z) with 'All' / 'All Prices' first
  const sortedOptions = useMemo(() => {
    if (!activeGroup || !Array.isArray(activeGroup.options)) return [];
    return [...activeGroup.options]
      .map((rawOpt) => normalizeOption(rawOpt))
      .sort((a, b) => {
        const aLabel = String(a.label || "").trim();
        const bLabel = String(b.label || "").trim();
        const aVal = String(a.value || "").trim().toLowerCase();
        const bVal = String(b.value || "").trim().toLowerCase();
        const aLower = aLabel.toLowerCase();
        const bLower = bLabel.toLowerCase();

        const isAAll = aLower === "all" || aLower.startsWith("all ") || aVal === "all";
        const isBAll = bLower === "all" || bLower.startsWith("all ") || bVal === "all";

        // Keep 'All' / 'All Prices' at the top if present
        if (isAAll && !isBAll) return -1;
        if (!isAAll && isBAll) return 1;

        return aLabel.localeCompare(bLabel, undefined, {
          sensitivity: "base",
          numeric: true,
        });
      });
  }, [activeGroup, normalizeOption]);

  return (
    <div className={`relative ${className}`}>
      <button
        ref={buttonRef}
        onClick={toggleOpen}
        className="app-button-secondary flex items-center justify-center gap-1.5 rounded-md w-8 h-8 p-0 sm:w-auto sm:h-auto sm:px-3 sm:py-1.5 text-xs font-semibold outline-none ring-0 focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 active:outline-none active:ring-0 disabled:opacity-60 shrink-0"
        type="button"
      >
        <div className="relative flex items-center justify-center">
          <MdTune className="text-[17px] sm:text-sm" />
          {totalAppliedCount > 0 && (
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-primary rounded-full sm:hidden" />
          )}
        </div>
        <span className="hidden sm:inline-flex sm:items-center sm:gap-1.5">
          <span>{loading ? "Loading..." : "Filter"}</span>
          {!loading && totalAppliedCount > 0 && (
            <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-primary text-on-primary text-[9px] font-bold leading-none shadow-sm animate-fade-in">
              {totalAppliedCount}
            </span>
          )}
        </span>
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={popoverRef}
            style={popoverStyle}
            className="app-glass-panel z-[9999] overflow-hidden animate-fade-in flex flex-col text-[11px] leading-relaxed shadow-xl border border-slate-200/80 rounded-lg bg-white"
          >
            <div className="flex flex-col h-full">
              {/* Main Body with Left Tabs and Right Options */}
              <div className="flex h-[200px] border-b border-slate-200/70">
                {/* Left Sidebar: Tabs */}
                <div className="w-1/3 bg-slate-50/80 border-r border-slate-200/70 flex flex-col overflow-y-auto py-1">
                  {filterGroups.map((group) => {
                    const groupKey = group.key || group.label?.toLowerCase();
                    const isActive = groupKey === activeTabKey;
                    const count = getTabSelectedCount(groupKey);

                    return (
                      <button
                        key={groupKey}
                        type="button"
                        onClick={() => setActiveTabKey(groupKey)}
                        className={`w-full text-left px-3 py-2 font-semibold transition-all flex items-center justify-between border-l-2 outline-none focus:outline-none ${
                          isActive
                            ? "bg-white border-primary text-primary font-bold shadow-sm"
                            : "border-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                        }`}
                      >
                        <span className="truncate max-w-[85px]">{group.label}</span>
                        {count > 0 && (
                          <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-primary text-on-primary text-[9px] font-bold leading-none shadow-sm animate-fade-in">
                            {count}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Right Panel: Options List */}
                <div className="w-2/3 p-3 overflow-y-auto flex flex-col">
                  <div className="flex justify-end items-center">
                    <button
                      type="button"
                      onClick={() => (handleClearActiveTab ? handleClearActiveTab() : handleClearFilters?.())}
                      className="text-[10px] text-primary hover:underline font-semibold outline-none focus:outline-none cursor-pointer"
                    >
                      Clear
                    </button>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    {loading ? (
                      <span className="text-slate-400 py-2">Loading ...</span>
                    ) : error ? (
                      <span className="text-red-500 py-2">Error loading ...</span>
                    ) : sortedOptions.length > 0 ? (
                      sortedOptions.map((opt) => {
                        const currentKey = activeGroup.key || activeGroup.label?.toLowerCase();
                        const isMulti = isGroupMultiple?.(activeGroup);
                        const selectedList = tempFilters[currentKey] || [];
                        const checked = selectedList.includes(opt.value);

                        return (
                          <button
                            key={opt.value}
                            type="button"
                            role={isMulti ? "checkbox" : "radio"}
                            aria-checked={checked}
                            onClick={() => handleOptionToggle(currentKey, opt.value)}
                            className="flex items-center gap-2 text-left text-slate-700 hover:text-primary transition-colors py-1 px-1 rounded hover:bg-slate-50 outline-none cursor-pointer"
                          >
                            {isMulti ? (
                              <span
                                className={`relative flex h-3.5 w-3.5 shrink-0 border rounded-[2px] transition-colors items-center justify-center ${
                                  checked
                                    ? "border-primary bg-primary text-on-primary"
                                    : "border-slate-300 bg-white"
                                }`}
                              >
                                {checked && (
                                  <MdCheck className="text-[10px] font-bold leading-none select-none" />
                                )}
                              </span>
                            ) : (
                              <span
                                className={`relative flex h-3.5 w-3.5 shrink-0 border rounded-full transition-colors items-center justify-center ${
                                  checked
                                    ? "border-primary bg-white"
                                    : "border-slate-300 bg-white"
                                }`}
                              >
                                {checked && (
                                  <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                                )}
                              </span>
                            )}
                            <span className="truncate text-xs">{opt.label}</span>
                          </button>
                        );
                      })
                    ) : (
                      <span className="text-slate-400 py-2">No options available</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Bottom Action Footer */}
              <div className="flex justify-between items-center px-3 py-2 bg-slate-50 border-t border-slate-200/70 w-full mt-auto">
                <button
                  type="button"
                  onClick={handleResetAll}
                  className="text-slate-500 hover:text-slate-800 text-xs font-semibold transition-all outline-none cursor-pointer"
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={handleApplyFilters}
                  className="app-button-primary px-3 py-1 rounded text-xs font-semibold outline-none cursor-pointer"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default FilterOptions;
